import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FieldPath } from 'react-hook-form';
import { Table } from 'reactstrap';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/sv';

import { ApplicationState } from 'store/redux';
import { IInvoiceFile } from 'pages/Invoices';
import { ISortState, SortService } from 'services/sortService';
import './InvoiceFiles.css';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.locale('sv');

export type IInvoiceFilesProps = {
  items?: IInvoiceFile[];
  onFileClick?: (invoiceId: string) => void;
};

const InvoiceFiles = function ({ items, onFileClick }: IInvoiceFilesProps) {
  const { t } = useTranslation('translation');
  const sortService = useMemo(() => SortService<IInvoiceFile>(), []);
  const [localState, setLocalState] = useState<ISortState<IInvoiceFile>>({
    sortedItems: !!items ? items : [],
    sortInstruction: { value: 'invoiceDate', asc: false },
    sortedColumn: 'orderNumber',
  });

  const onDownloadClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    invoiceId: string
  ) => {
    event.preventDefault();
    const target = event.currentTarget as HTMLAnchorElement;
    if (!target) {
      alert('target was empty');
      console.debug(target);
    }
    if (onFileClick) {
      onFileClick(invoiceId);
    }
  };

  useEffect(() => {
    const stateCopy = { ...localState };
    stateCopy.sortedItems = !!items ? sortService.sortItems(items, stateCopy.sortInstruction) : [];
    setLocalState(stateCopy);
  }, [items, sortService]);

  const onHeaderClick = (
    columnName: string,
    value: FieldPath<IInvoiceFile> | ((item: IInvoiceFile) => any)
  ) => {
    const stateCopy = { ...localState };
    stateCopy.sortInstruction = {
      value: value,
      asc: stateCopy.sortedColumn === columnName ? !stateCopy.sortInstruction.asc : true,
    };
    stateCopy.sortedItems = !!items ? sortService.sortItems(items, stateCopy.sortInstruction) : [];
    stateCopy.sortedColumn = columnName;
    setLocalState(stateCopy);
  };

  const getClass: (columnName: string) => string = (columnName: string) => {
    return (
      'sortable' +
      (localState.sortedColumn === columnName
        ? ' sorted' + (localState.sortInstruction.asc ? ' asc' : ' desc')
        : '')
    );
  };

  return (
    <div className="row">
      <div className="col fullwidth-table-container collapsable-table-md">
        <Table hover className="fullwidth-table invoice-file-table" striped={true}>
          <thead>
            <tr>
              <th
                className={getClass('description')}
                onClick={() => onHeaderClick('description', 'description')}
              >
                {t('invoices.INVOICE_DESCRIPTION')}
              </th>
              <th
                className={getClass('category')}
                onClick={() => onHeaderClick('category', 'category')}
              >
                {t('invoices.INVOICE_CATEGORY')}
              </th>
              <th
                className={getClass('invoiceDate')}
                onClick={() => onHeaderClick('invoiceDate', 'invoiceDate')}
              >
                {t('invoices.INVOICE_DATE')}
              </th>
              <th
                className={getClass('company')}
                onClick={() => onHeaderClick('company', 'company')}
              >
                {t('invoices.INVOICE_UNIT')}
              </th>
            </tr>
          </thead>
          <tbody>
            {localState.sortedItems &&
              localState.sortedItems.map((i: IInvoiceFile) => (
                <tr key={i.invoiceId}>
                  <td className="file-item">
                    <a
                      onClick={(event) => onDownloadClick(event, i.invoiceId)}
                      href={i.downloadLink}
                    >
                      {i.description}
                    </a>
                  </td>
                  <td>
                    <span className="collapsed-title">{t('invoices.INVOICE_CATEGORY')}: </span>
                    {i.category}
                  </td>
                  <td>
                    <span className="collapsed-title">{t('invoices.INVOICE_DATE')}: </span>
                    {dayjs(i.invoiceDate).format('YYYY-MM-DD')}
                  </td>
                  <td>
                    <span className="collapsed-title">{t('invoices.INVOICE_UNIT')}: </span>
                    {i.company}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default connect((appState: ApplicationState) => ({ alert: appState.alert }))(InvoiceFiles);
